import { Button, IconFont, Popover } from '@byecode/ui'
import type { Field } from '@lighthouse/core'
import { type DataSourceAbstract, type VariableADTvalue, VariableType } from '@lighthouse/core'
import type { FlowNode, VariableTree2, Workflow } from '@lighthouse/shared'
import {
    addresseeFieldType,
    getUpstreamNodesOptions,
    getUserDatasourceOptions,
    innerTypeToFieldTypeMapInCondition,
    USER_DATASOURCE,
    VariableSelectDropDown,
    VariableTag
} from '@lighthouse/shared'
import { clone, find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { DepartmentTag } from './DepartmentTag'
import { PersonTag } from './PersonTag'
import { RoleTag } from './RoleTag'
import { Node } from './styles'

const SCxContainer = styled.div``

const SCxPersonList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`

const SCxButtonContent = styled(Button)``

const SCxIcon = styled(IconFont)`
    margin-right: 6px;
`

const SCxButtonContainer = styled.div`
    margin: 4px 0;
`

interface PersonItemProps {
    disabled?: boolean
    data: VariableADTvalue
    upstreamOptions: VariableTree2[]
    userOption?: VariableTree2
    onClose: () => void
}

const PersonItem: React.FC<PersonItemProps> = ({ disabled, data, upstreamOptions, userOption, onClose }) => {
    return useMemo(() => {
        if (data.type === VariableType.VALUE) {
            if (data.valueVariable?.type === 'role') {
                return <RoleTag withClose={!disabled} data={data} onClose={onClose} />
            }
            if (data.valueVariable?.type === 'department') {
                return <DepartmentTag withClose={!disabled} data={data} onClose={onClose} />
            }
            return <PersonTag withClose={!disabled} data={data} onClose={onClose} />
        }
        if (data.type === VariableType.ARG_VARIABLE || data.type === VariableType.UPSTREAM || data.type === VariableType.USER) {
            return (
                <Node>
                    <VariableTag
                        value={data}
                        upstreamOptions={upstreamOptions}
                        userOption={userOption}
                        withClose={!disabled}
                        onClose={onClose}
                    />
                </Node>
            )
        }
        return null
    }, [data, disabled, onClose, upstreamOptions, userOption])
}

interface MessageTargetPersonProps {
    value: VariableADTvalue[] | undefined
    parentNodes: FlowNode[] | undefined
    dataSourceList: DataSourceAbstract[]
    workflowList: Workflow[]
    onChange: (val: VariableADTvalue[]) => void
}

const fields: Field[] = [
    { id: 'person', dsId: '', type: 'person', innerType: 'ARRAY', name: '成员', person: {} },
    { id: 'role', dsId: '', type: 'role', innerType: 'ARRAY', name: '角色' },
    { id: 'department', dsId: '', type: 'department', innerType: 'ARRAY', name: '部门' }
]

export const MessageTargetPerson: React.FC<MessageTargetPersonProps> = ({ value, parentNodes, dataSourceList, workflowList, onChange }) => {
    const [open, setOpen] = useState(false)
    const disabledWithVersion = useIsDisabledWithVersion()
    const upstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: parentNodes || [],
                allUpstreamNodes: parentNodes || [],
                dataSourceList
            }),
        [dataSourceList, parentNodes, workflowList]
    )

    const userOption = useMemo(() => {
        const userDataSource = find(item => item.id === USER_DATASOURCE, dataSourceList)
        return getUserDatasourceOptions({
            dataSource: userDataSource
        })
    }, [dataSourceList])

    const getSplitValue = useCallback((val: VariableADTvalue) => {
        if (val.type === VariableType.VALUE && val.valueVariable?.type === 'department') {
            const ids = val.valueVariable.value
            return ids.map<VariableADTvalue>(id => ({
                type: VariableType.VALUE,
                valueVariable: {
                    type: 'department',
                    value: [id]
                }
            }))
        }
        return [val]
    }, [])

    const handleChange = useCallback(
        (val: VariableADTvalue) => {
            setOpen(false)
            const originValue = clone(value || [])
            const result = getSplitValue(val)
            const stringValue = originValue?.map(item => JSON.stringify(item)) || []
            result.forEach(v => {
                if (stringValue.includes(JSON.stringify(v))) {
                    return
                }
                originValue.push(v)
                stringValue.push(JSON.stringify(v))
            })
            onChange(originValue)
        },
        [getSplitValue, onChange, value]
    )

    const handleDelete = useCallback(
        (i: number) => {
            if (!value) {
                return
            }
            const newValue = clone(value)
            newValue?.splice(i, 1)
            onChange(newValue)
        },
        [onChange, value]
    )

    return (
        <SCxContainer>
            <SCxPersonList>
                {value?.map((item, index) => (
                    <PersonItem
                        disabled={disabledWithVersion}
                        key={index}
                        data={item}
                        upstreamOptions={upstreamOptions}
                        userOption={userOption}
                        onClose={() => handleDelete(index)}
                    />
                ))}
            </SCxPersonList>
            <Popover position="bottom-start" disabled={disabledWithVersion} width={248} withinPortal opened={open} onChange={setOpen}>
                <Popover.Target>
                    <SCxButtonContainer>
                        <SCxButtonContent
                            disabled={disabledWithVersion}
                            radius={100}
                            icon={<SCxIcon type="Add" fill="var(--color-gray-500)" size={16} />}
                        >
                            添加
                        </SCxButtonContent>
                    </SCxButtonContainer>
                </Popover.Target>
                <Popover.Dropdown>
                    <VariableSelectDropDown
                        innerTypes={['TEXT', 'ARRAY']}
                        fields={fields}
                        upstreamOptions={upstreamOptions}
                        userOption={userOption}
                        onChange={handleChange}
                    />
                </Popover.Dropdown>
            </Popover>
        </SCxContainer>
    )
}
